import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import MicIcon from "@mui/icons-material/Mic";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import { Box, Button, Paper, Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Cookies from "js-cookie";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import GeneratedVoicesModal from "../../../../components/common/GeneratedVoicesModal";
import { useAlert } from "../../../../context/AlertContext";
import { webmToWav } from "../../../../utils/audioConverter";
import { ColumnCenteredBox, RowCenteredBox } from "../../../../utils/styledBox";
import { alpha } from "@mui/material/styles";
import { VoiceProvider } from "../../../../context/VoiceContext";

interface AudioUploadProps {
  audio: string;
  setAudio: (audio: string) => void;
  audioDuration?: number;
  setAudioDuration?: (duration: number) => void;
  voice?: boolean;
}

const AudioUpload = ({ audio, setAudio, audioDuration, setAudioDuration, voice = true }: AudioUploadProps) => {
  const [open, setOpen] = React.useState(false);
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const { setAlert } = useAlert();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [silenceTimeout, setSilenceTimeout] = useState<NodeJS.Timeout | null>(null);
  const [recordingDuration, setRecordingDuration] = useState<number>(0); // Re-add state for recording duration
  const [localAudioDuration, setLocalAudioDuration] = useState<number>(0);
  const chunks = useRef<Blob[]>([]);
  const recordingTimer = useRef<NodeJS.Timer | null>(null); // Re-add ref for timer
  const currentDuration = useRef<number>(0);

  // Sync local state with props when audioDuration changes
  React.useEffect(() => {
    if (audioDuration && audioDuration > 0 && audioDuration !== localAudioDuration) {
      setLocalAudioDuration(audioDuration);
    }
  }, [audioDuration]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      setMediaRecorder(recorder);
      chunks.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.current.push(e.data);
        }
      };

      recorder.onstop = async () => {
        try {
          // WebMからWavに変換
          const webmBlob = new Blob(chunks.current, { type: "audio/webm" });
          const wavBlob = await webmToWav(webmBlob);

          // Base64に変換
          const base64Data = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target?.result as string);
            reader.readAsDataURL(wavBlob);
          });

          // Create temporary audio element to get duration from the final blob
          const tempAudioForDuration = document.createElement("audio");
          tempAudioForDuration.src = URL.createObjectURL(wavBlob); // Use the wavBlob directly

          const processDuration = (durationValue: number) => {
            let finalDuration = durationValue;
            // If invalid duration, fall back to timer
            if (!isFinite(finalDuration) || finalDuration <= 0) {
              finalDuration = Math.max(1, Math.ceil(currentDuration.current)); // Ensure at least 1s if needed
            } else {
              finalDuration = Math.ceil(finalDuration); // Use ceil for valid duration
            }

            console.log("Setting duration:", finalDuration);
            setLocalAudioDuration(finalDuration);
            if (setAudioDuration) {
              setAudioDuration(finalDuration);
            }

            // Cleanup URL object
            URL.revokeObjectURL(tempAudioForDuration.src);

            // Always set audio state AFTER duration is processed
            setAudio(base64Data);
          };

          tempAudioForDuration.onloadedmetadata = () => {
            processDuration(tempAudioForDuration.duration);
          };

          tempAudioForDuration.onerror = () => {
            console.error("Error loading recorded blob for duration calculation, using timer fallback");
            processDuration(NaN); // Will trigger fallback in processDuration
          };

          // ストリームを停止
          stream.getTracks().forEach((track) => track.stop());

          // Reset timer ref
          currentDuration.current = 0;
        } catch (error) {
          console.error("Failed to convert audio: ", error);
          setAlert("error", "Failed to convert audio");
        }
      };

      // 無音検出の設定
      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);

      let silenceStartTime: number | null = null;
      const startTime = Date.now();
      currentDuration.current = 0;
      setRecordingDuration(0); // Reset display duration

      // Start recording timer
      recordingTimer.current = setInterval(() => {
        const newDuration = currentDuration.current + 1;
        currentDuration.current = newDuration; // Update ref directly
        setRecordingDuration(newDuration); // Update state for display
      }, 1000);

      const checkSilence = () => {
        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;

        // 60sの制限チェック
        if (Date.now() - startTime >= 60000) {
          // Stop timer when stopping recording due to limit
          if (recordingTimer.current) {
            clearInterval(recordingTimer.current);
            recordingTimer.current = null;
          }
          recorder.stop();
          setIsRecording(false);
          return; // Stop checking silence
        }

        if (average < 2) {
          if (silenceStartTime === null) {
            silenceStartTime = Date.now();
          } else if (Date.now() - silenceStartTime >= 1500) {
            // Stop timer when stopping recording due to silence
            if (recordingTimer.current) {
              clearInterval(recordingTimer.current);
              recordingTimer.current = null;
            }
            recorder.stop();
            setIsRecording(false);
            return; // Stop checking silence
          }
        } else {
          silenceStartTime = null;
        }

        if (isRecording) {
          requestAnimationFrame(checkSilence);
        }
      };

      recorder.start();
      setIsRecording(true);
      checkSilence();
    } catch (err) {
      console.error("録音の開始に失敗しました:", err);
      setAlert("error", "Failed to start recording");
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state === "recording") {
      mediaRecorder.stop();
      setIsRecording(false);
      if (silenceTimeout) {
        clearTimeout(silenceTimeout);
        setSilenceTimeout(null);
      }
      // Stop timer when manually stopping recording
      if (recordingTimer.current) {
        clearInterval(recordingTimer.current);
        recordingTimer.current = null;
      }
    }
  };

  // Handle duration for UPLOADED files and clearing audio state
  React.useEffect(() => {
    const audioElement = audioRef.current;

    // Define fetchMetadata for uploaded files
    const fetchMetadata = async () => {
      // Ensure we only fetch for non-recorded audio and if audio exists
      if (!audio || audio.startsWith("data:audio/wav")) {
        return;
      }
      const csrftoken = Cookies.get("csrftoken");
      try {
        const response = await fetch("/api/v1/juno/audio-metadata", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrftoken!,
          },
          body: JSON.stringify({
            audio1: audio, // Send the base64 string
          }),
        });
        const data = await response.json();
        if (data.status === "success" && data.metadata) {
          let duration = data.metadata.duration;
          if (typeof duration === "number" && isFinite(duration) && duration > 0) {
            duration = Math.ceil(duration); // Use ceil
            setLocalAudioDuration(duration);
            if (setAudioDuration) {
              setAudioDuration(duration);
            }
          } else {
            console.warn("Received invalid duration from metadata API:", data.metadata.duration);
            // If API fails and current duration is 0, explicitly set a minimum of 1s
            if (localAudioDuration <= 0) {
              setLocalAudioDuration(1);
              if (setAudioDuration) {
                setAudioDuration(1);
              }
            }
          }
        } else {
          console.warn("Metadata API call did not return success or metadata:", data);
          if (localAudioDuration <= 0) {
            setLocalAudioDuration(1);
            if (setAudioDuration) {
              setAudioDuration(1);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching audio metadata:", error);
        if (localAudioDuration <= 0) {
          setLocalAudioDuration(1);
          if (setAudioDuration) {
            setAudioDuration(1);
          }
        }
      }
    };

    if (audio && audioElement) {
      const isRecorded = audio.startsWith("data:audio/wav");

      // For recorded audio, duration is set in onstop, so we only need to set the src for playback
      if (isRecorded) {
        // Ensure the src is set for the audio controls if it changed (though unlikely for recorded)
        if (audioElement.src !== audio) {
          audioElement.src = audio;
        }
        return; // Skip listener setup and API fetch for recorded audio
      }

      // --- Logic for UPLOADED audio ---
      const handleMetadataLoaded = () => {
        // Trust the element's duration if it's valid and positive
        if (isFinite(audioElement.duration) && audioElement.duration > 0) {
          const duration = Math.ceil(audioElement.duration);
          setLocalAudioDuration(duration);
          if (setAudioDuration) {
            setAudioDuration(duration);
          }
        } else {
          // If element duration is invalid for uploaded file, try API
          fetchMetadata();
        }
      };

      // Add listener before setting src
      audioElement.addEventListener("loadedmetadata", handleMetadataLoaded);

      // Set the source only if it's different
      if (audioElement.src !== audio) {
        // Reset duration while new source loads (only for uploaded files)
        setLocalAudioDuration(0);
        audioElement.src = audio;
        // Consider adding: audioElement.load();
      } else {
        // If src is the same, check if metadata needs to be re-evaluated
        if (audioElement.readyState >= 1 && localAudioDuration <= 0) {
          handleMetadataLoaded();
        }
      }

      // Cleanup function
      return () => {
        audioElement.removeEventListener("loadedmetadata", handleMetadataLoaded);
      };
    } else {
      // Audio is cleared
      setLocalAudioDuration(0);
      if (setAudioDuration) {
        setAudioDuration(0);
      }
    }
    // Only re-run if the audio source itself changes
  }, [audio, setAudioDuration]);

  // Cleanup timer on unmount
  React.useEffect(() => {
    return () => {
      if (recordingTimer.current) {
        clearInterval(recordingTimer.current);
      }
    };
  }, []);

  React.useEffect(() => {
    const audioElement = audioRef.current;
    if (!audioElement) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);

    audioElement.addEventListener("play", handlePlay);
    audioElement.addEventListener("pause", handlePause);
    audioElement.addEventListener("ended", handleEnded);

    return () => {
      audioElement.removeEventListener("play", handlePlay);
      audioElement.removeEventListener("pause", handlePause);
      audioElement.removeEventListener("ended", handleEnded);
    };
  }, [audio]);

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files && files[0]) {
      const file = files[0];
      updateAudio(file);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files[0]) {
      const file = files[0];
      updateAudio(file);
    }
  };

  const updateAudio = (file: File) => {
    // より包括的なMIMEタイプチェック（Safariでの互換性向上）
    const allowedMimeTypes = ["audio/mp3", "audio/mpeg", "audio/wav", "audio/wave", "audio/x-wav"];

    // ファイル拡張子による二重チェック
    const fileName = file.name.toLowerCase();
    const allowedExtensions = [".mp3", ".wav"];
    const hasValidExtension = allowedExtensions.some((ext) => fileName.endsWith(ext));

    // デバッグ用ログ（Safari対応確認のため）
    console.log("File type:", file.type, "File name:", file.name);

    // MIMEタイプまたは拡張子のいずれかが有効であればOK
    const hasValidMimeType = allowedMimeTypes.includes(file.type);

    if (!hasValidMimeType && !hasValidExtension) {
      setAlert("error", t("juno.input.uploadError.audioType"));
      return;
    }

    if (file.size > 31457280) {
      setAlert("error", t("juno.video.error.max.audioSize"));
      return;
    }

    const audio = document.createElement("audio");
    audio.preload = "metadata";

    audio.onloadedmetadata = () => {
      URL.revokeObjectURL(audio.src);

      if (voice) {
        // 動画生成時のチェック
        if (audio.duration > 60) {
          setAlert("error", t("juno.video.error.max.audioDuration"));
          return;
        }
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          setAudio(e.target.result as string);
        }
      };
      reader.readAsDataURL(file);
    };

    audio.src = URL.createObjectURL(file);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileSelect = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (fileInputRef.current) {
      fileInputRef.current.click();
    } else {
      console.error("fileInputRef is null");
    }
  };

  const handleDelete = (event: React.MouseEvent) => {
    event.stopPropagation();
    setAudio("");
  };

  const renderUploadArea = () => (
    <Box
      sx={{
        display: "flex",
        width: 130,
        height: 130,
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.5),
        aspectRatio: "1 / 1",
        outline: (theme) => `1px solid ${alpha(theme.palette.text.primary, 0.2)}`,
        borderRadius: 1,
      }}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={handleFileSelect}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: "100%",
          p: 2,
          "&:hover": {
            cursor: "pointer",
          },
          position: "relative",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <CloudUploadIcon fontSize={"large"} sx={{ mb: 2 }} />
            <Typography variant={"caption"} color={"text.secondary"}>
              Upload Audio
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const renderAudioControls = () => (
    <Paper
      variant="outlined"
      sx={{
        display: "flex",
        width: 130,
        height: 130,
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.5),
        aspectRatio: "1 / 1",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          height: 130,
          alignItems: "center",
          width: 130,
          aspectRatio: "1 / 1",
          "&:hover": {
            cursor: "pointer",
          },
          position: "relative",
        }}
      >
        <audio ref={audioRef} src={audio} style={{ display: "none" }} />
        {/* Audio waveform icon in the middle */}
        <GraphicEqIcon sx={{ fontSize: 46, color: "rgba(255, 255, 255, 0.7)" }} />

        {/* Play/Stop Button (centered overlay) */}
        <IconButton
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backdropFilter: "blur(5px)",
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            transition: "0.3s",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.4)",
            },
          }}
          onClick={(event) => {
            event.stopPropagation();
            const audioElement = audioRef.current;
            if (!audioElement) return;

            if (!isPlaying) {
              audioElement.play().catch(console.error);
              setIsPlaying(true);
            } else {
              audioElement.pause();
              setIsPlaying(false);
            }
          }}
        >
          {!isPlaying ? <PlayArrowIcon style={{ color: "white" }} /> : <StopIcon style={{ color: "white" }} />}
        </IconButton>

        {/* Duration indicator (Bottom Left) */}
        <Box
          sx={{
            position: "absolute",
            bottom: 8,
            left: 8,
            right: 8,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              backdropFilter: "blur(5px)",
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              color: "white",
              padding: "4px 8px",
              borderRadius: 1,
              fontSize: "0.8rem",
            }}
          >
            {localAudioDuration} s
          </Typography>
        </Box>

        {/* Delete Button (Bottom Right) */}
        <IconButton
          sx={{
            position: "absolute",
            bottom: 8,
            right: 8,
            backdropFilter: "blur(5px)",
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            transition: "0.3s",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.4)",
            },
          }}
          size="small"
          onClick={handleDelete}
        >
          <DeleteIcon style={{ color: "white" }} fontSize="small" />
        </IconButton>
      </Box>
    </Paper>
  );

  const handleOpenModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setOpen(true);
  };

  return (
    <RowCenteredBox sx={{ gap: 2, justifyContent: "space-between", alignItems: "center", mb: 1 }}>
      <input
        type="file"
        style={{ display: "none" }}
        onChange={handleChange}
        accept=".mp3,.wav,audio/mp3,audio/mpeg,audio/wav,audio/wave"
        ref={fileInputRef}
      />
      {audio ? renderAudioControls() : renderUploadArea()}

      <ColumnCenteredBox>
        <Button
          variant="text"
          onClick={handleFileSelect}
          startIcon={<CloudUploadIcon />}
          sx={{ px: 1, mb: 1, whiteSpace: "nowrap", width: "100%", maxWidth: 200 }}
          size="small"
        >
          {t("juno.video.params.audio")}
        </Button>

        {voice && (
          <>
            <Button
              variant="text"
              onClick={handleOpenModal}
              startIcon={<GraphicEqIcon />}
              sx={{ px: 1, mb: 1, whiteSpace: "nowrap", width: "100%", maxWidth: 200 }}
              size="small"
            >
              {t("juno.video.params.generatedAudio")}
            </Button>

            <Button
              variant="text"
              onClick={(event) => {
                event.stopPropagation();
                if (!isRecording) {
                  startRecording();
                } else {
                  stopRecording();
                }
              }}
              startIcon={isRecording ? <StopIcon /> : <MicIcon />}
              sx={{
                px: 1,
                mb: 1,
                whiteSpace: "nowrap",
                width: "100%",
                maxWidth: 200,
                color: isRecording ? "error.main" : "primary.main",
              }}
              size="small"
            >
              {isRecording ? `Recording... (${recordingDuration}s)` : "Record Voice"}
            </Button>
            <VoiceProvider>
              <GeneratedVoicesModal open={open} setOpen={setOpen} setAudio={setAudio} />
            </VoiceProvider>
          </>
        )}
      </ColumnCenteredBox>
    </RowCenteredBox>
  );
};

export default AudioUpload;
